import React, { useEffect, useState } from 'react';
import axios from 'axios';
import socket from './socket';
import { BASE_URL } from '../config/api';
import { useUser } from '../contexts/UserContext';
import './Chats.css';

const Chats = () => {
  const { userData } = useUser();
  const [users, setUsers] = useState([]);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Build a robust base URL like other hooks
  const baseURL = BASE_URL.replace(/\/+$/, '') + '/';

  useEffect(() => {
    if (!userData) return;

    // Fetch users from backend (endpoint may vary on your server)
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await axios.get(`${baseURL}auth/users2`, { withCredentials: true });
        // Expecting array of user objects
        const list = Array.isArray(res.data) ? res.data : res.data.users || [];
        // Remove current user from list
        const filtered = list.filter((u) => (u.email || u.id) !== (userData.email || userData.id));
        setUsers(filtered);
      } catch (err) {
        console.error('Failed to fetch users for chat:', err.message || err);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();

    // Join socket room representing this user
    if (socket && socket.connected) {
      socket.emit('user:join', { id: userData.id, email: userData.email });
    } else if (socket) {
      socket.on('connect', () => {
        socket.emit('user:join', { id: userData.id, email: userData.email });
      });
    }

    // Listen for incoming private messages
    socket.on('private-message', (msg) => {
      // If message is for current active chat partner, append
      const isRelevant =
        (msg.from === (activeUser?.id || activeUser?.email) && msg.to === (userData.id || userData.email)) ||
        (msg.to === (activeUser?.id || activeUser?.email) && msg.from === (userData.id || userData.email));

      if (isRelevant) {
        setMessages((m) => [...m, msg]);
      }
    });

    return () => {
      socket.off('private-message');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, activeUser]);

  useEffect(() => {
    // load messages when active user changes
    if (!activeUser || !userData) return;

    const loadMessages = async () => {
      try {
        const res = await axios.get(
          `${baseURL}messages/history?user1=${encodeURIComponent(userData.id || userData.email)}&user2=${encodeURIComponent(
            activeUser.id || activeUser.email
          )}`,
          { withCredentials: true }
        );
        setMessages(res.data.messages || res.data || []);
      } catch (err) {
        console.warn('No prior messages or failed to load:', err.message || err);
        setMessages([]);
      }
    };

    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser]);

  const sendMessage = async () => {
    if (!text.trim() || !activeUser || !userData) return;

    const payload = {
      from: userData.id || userData.email,
      to: activeUser.id || activeUser.email,
      text: text.trim(),
      createdAt: new Date().toISOString(),
    };

    // Emit via socket for real-time
    try {
      socket.emit('private-message', payload);
    } catch (err) {
      console.warn('Socket emit failed', err.message || err);
    }

    // Persist to backend (best-effort)
    try {
      await axios.post(`${baseURL}messages/send`, payload, { withCredentials: true });
    } catch (err) {
      console.warn('Failed to persist message to server:', err.message || err);
    }

    setMessages((m) => [...m, payload]);
    setText('');
  };

  if (!userData) {
    return <div className="chats-empty" style={{ padding: 16 }}>Please log in to access chats.</div>;
  }

  return (
    <div className="chats-container">
      <div className="chats-contacts">
        <h3 style={{ marginTop: 4 }}>Contacts</h3>
        {loadingUsers ? (
          <div>Loading...</div>
        ) : users.length === 0 ? (
          <div className="no-users">No other users found.</div>
        ) : (
          <ul className="contacts-list">
            {users.map((u) => (
              <li
                key={u.id || u.email}
                onClick={() => setActiveUser(u)}
                className={`contacts-list-item ${activeUser && (activeUser.id || activeUser.email) === (u.id || u.email) ? 'active' : ''}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{u.firstname || u.name || u.email}</strong>
                    <div className="contacts-email">{u.email}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="chats-main">
        <div className="chats-header">
          <h4 style={{ margin: 0 }}>{activeUser ? (activeUser.firstname || activeUser.name || activeUser.email) : 'Select a contact'}</h4>
        </div>

        <div className="chats-body">
          {activeUser ? (
            messages.length === 0 ? (
              <div style={{ color: '#666' }}>No messages yet. Say hi 👋</div>
            ) : (
              messages.map((m, i) => {
                const mine = (m.from || m.sender) === (userData.id || userData.email);
                return (
                  <div key={i} className={`message ${mine ? 'mine' : ''}`}>
                    <div className={`bubble ${mine ? 'mine' : 'theirs'}`}>
                      <div style={{ whiteSpace: 'pre-wrap' }}>{m.text || m.message || m.body}</div>
                      <div className="bubble-time">{new Date(m.createdAt || m.timestamp || Date.now()).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            <div style={{ padding: 20, color: '#666' }}>Choose a contact on the left to start chatting.</div>
          )}
        </div>

        <div className="chats-input">
          <input
            className="chats-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendMessage();
            }}
            placeholder={activeUser ? `Message ${activeUser.firstname || activeUser.email}` : 'Select a contact to message'}
            disabled={!activeUser}
          />
          <button onClick={sendMessage} disabled={!activeUser || !text.trim()} className="send-button">
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chats;
